import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';

export interface CollectionListing { id: number; title: string; slug: string; imageUrl?: string | null; imageSizes?: Record<string, unknown> | null; }
export interface UserCollection { id: number; name: string; slug?: string; description?: string; shareWith?: 'everyone'|'followers'|'private'|string|null; itemCount?: number; items?: CollectionListing[]; }

@Injectable({ providedIn: 'root' })
export class FavoriteCollectionService {
  private readonly api = inject(ApiClientService);

  async collections(): Promise<UserCollection[]> {
    const response = await firstValueFrom(this.api.post<any>('/api/collections', {}));
    const rows = response?.data?.collections ?? response?.apiData?.collections ?? response?.collections ?? [];
    return Array.isArray(rows) ? rows.map((item: any) => this.normalizeCollection(item)).filter((item: UserCollection) => item.id > 0 && item.name) : [];
  }

  async favoriteState(listingID: number, slug = ''): Promise<Record<string, boolean>> {
    const response = await firstValueFrom(this.api.post<any>('/api/collections/favorite-state', { listingID, slug }));
    return response?.data?.favorites ?? response?.apiData?.favorites ?? response?.favorites ?? {};
  }

  async toggleFavorite(listingID: number, collectionID: number | null = null): Promise<void> {
    await firstValueFrom(this.api.post('/api/collections/favorite', { listing: listingID, collection: collectionID ? String(collectionID) : '' }));
  }

  async createCollection(name: string, description = '', shareWith: 'everyone'|'followers'|'private' = 'private'): Promise<UserCollection | null> {
    const response = await firstValueFrom(this.api.post<any>('/api/collections/create', { name, description, shareWith }));
    const item = response?.data?.newCollection ?? response?.apiData?.newCollection ?? response?.newCollection;
    return item?.id ? this.normalizeCollection(item) : null;
  }

  async updateCollection(collection: UserCollection): Promise<void> {
    await firstValueFrom(this.api.post('/api/collections/update', { collectionID: collection.id, name: collection.name, description: collection.description ?? '', shareWith: collection.shareWith ?? 'private' }));
  }

  async deleteCollection(collectionID: number): Promise<void> {
    await firstValueFrom(this.api.post('/api/collections/delete', { collectionID }));
  }

  async publicCollection(collectionID: number, slug: string): Promise<UserCollection | null> {
    const response = await firstValueFrom(this.api.post<any>('/api/collections/public', { collectionID, slug }));
    const item = response?.data?.collection ?? response?.apiData?.collection ?? response?.collection;
    return item?.id ? this.normalizeCollection(item) : null;
  }

  private normalizeCollection(item: any): UserCollection {
    return {
      id: Number(item?.id ?? 0), name: String(item?.name ?? ''), slug: String(item?.slug ?? ''),
      description: String(item?.description ?? ''), shareWith: item?.shareWith ?? 'private', itemCount: Number(item?.itemCount ?? 0),
      items: Array.isArray(item?.items) ? item.items.map((listing: any) => ({ id: Number(listing.id), title: String(listing.title ?? ''), slug: String(listing.slug ?? ''), imageUrl: listing.imageUrl ?? null, imageSizes: listing.imageSizes ?? null })) : undefined
    };
  }
}
